import { useQueries } from '@tanstack/react-query'
import { useEffect } from 'react'
import { message } from 'antd'
import { buildEntityQuery } from '@/shared/api/buildEntityQuery'
import { getEndpointName } from '@/shared/api/sourceApi'
import { SYSTEM_TYPE } from '@/app/store/systemStore'
import {
  SourceAccount,
  SourceVehicle,
  SourceDriver,
  SourceUser,
  SourceGeozone,
  SourceRetranslation,
} from '@/app/types/source'

const QUERY_KEY_PREFIX = 'wialon-source'
const EMPTY_ARRAY: never[] = []

export interface WialonSourceData {
  agents: SourceAccount[]
  vehicles: SourceVehicle[]
  drivers: SourceDriver[]
  users: SourceUser[]
  geozones: SourceGeozone[]
  retranslations: SourceRetranslation[]
  isLoading: boolean
  isError: boolean
}

export const useWialonSourceQueries = (
  token: string,
  sourceApiUrl: string,
  systemUrl: string
): WialonSourceData => {
  const authParam = token || null

  const [agents, vehicles, drivers, users, geozones, retranslations] =
    useQueries({
      queries: [
        buildEntityQuery<SourceAccount>(
          'Agents',
          authParam,
          sourceApiUrl,
          systemUrl,
          SYSTEM_TYPE.WIALON,
          QUERY_KEY_PREFIX
        ),
        buildEntityQuery<SourceVehicle>(
          'Vehicles',
          authParam,
          sourceApiUrl,
          systemUrl,
          SYSTEM_TYPE.WIALON,
          QUERY_KEY_PREFIX
        ),
        buildEntityQuery<SourceDriver>(
          'Drivers',
          authParam,
          sourceApiUrl,
          systemUrl,
          SYSTEM_TYPE.WIALON,
          QUERY_KEY_PREFIX
        ),
        buildEntityQuery<SourceUser>(
          'Users',
          authParam,
          sourceApiUrl,
          systemUrl,
          SYSTEM_TYPE.WIALON,
          QUERY_KEY_PREFIX
        ),
        buildEntityQuery<SourceGeozone>(
          'Geozones',
          authParam,
          sourceApiUrl,
          systemUrl,
          SYSTEM_TYPE.WIALON,
          QUERY_KEY_PREFIX
        ),
        buildEntityQuery<SourceRetranslation>(
          'Retranslations',
          authParam,
          sourceApiUrl,
          systemUrl,
          SYSTEM_TYPE.WIALON,
          QUERY_KEY_PREFIX
        ),
      ],
    })

  useEffect(() => {
    const queryResults: Array<[string, typeof agents]> = [
      ['Agents', agents],
      ['Vehicles', vehicles],
      ['Drivers', drivers],
      ['Users', users],
      ['Geozones', geozones],
      ['Retranslations', retranslations],
    ]

    const failedEntities = queryResults
      .filter(([, result]) => result.data?.error)
      .map(([endpoint]) => getEndpointName(endpoint))

    if (failedEntities.length > 0) {
      message.error(`Не удалось загрузить: ${failedEntities.join(', ')}`)
    }
  }, [
    agents.data,
    vehicles.data,
    drivers.data,
    users.data,
    geozones.data,
    retranslations.data,
  ])

  return {
    agents: agents.data?.data ?? EMPTY_ARRAY,
    vehicles: vehicles.data?.data ?? EMPTY_ARRAY,
    drivers: drivers.data?.data ?? EMPTY_ARRAY,
    users: users.data?.data ?? EMPTY_ARRAY,
    geozones: geozones.data?.data ?? EMPTY_ARRAY,
    retranslations: retranslations.data?.data ?? EMPTY_ARRAY,
    isLoading: [
      agents,
      vehicles,
      drivers,
      users,
      geozones,
      retranslations,
    ].some((r) => r.isLoading),
    isError: [agents, vehicles, drivers, users, geozones, retranslations].some(
      (r) => r.isError
    ),
  }
}
